
import Vue from 'vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import {  Project, Tag } from '../../types/main'
import { ActivationToEdit } from '../../types/GCB2'
import uniq from 'lodash/uniq'
import toNumber from 'lodash/toNumber'
import cloneDeep from 'lodash/cloneDeep'
import omitBy from 'lodash/omitBy'
import isUndefined from 'lodash/isUndefined'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import SpinnerLoader from '../../components/SpinnerLoader.vue'
import ActivationsEditTable from '@root/src/components/chisai/GCB2/ActivationsEdit/ActivationsEditTable.vue'
import { getActivations, bulkEditActivations, setChangedActivationNotification, getActivationsTags, getFreeSlots } from '@root/src/api/chisai/GCB2'
import { ModalButtonsParams, useModalStore } from '@/store/stores/modal'
import SmallHint from '@root/src/components/SmallHint.vue'
import cfg from '../../../config'
import CustomActivatorSelect from '@root/src/components/inputs/CustomActivatorSelect.vue'
import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
import { localStorageCache } from '@/helpers/localStorageCache'
import DeletionActivationsModal from '@root/src/components/chisai/GCB2/changeActivation/DeletionActivationsModal.vue'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { editActivationService } from '@root/src/services/editActivation'
import isObject from 'lodash/isObject'

export default Vue.extend({
    name: 'MassActivationEdit',
    components: {Breadcrumbs, SpinnerLoader, ActivationsEditTable, SmallHint, BaseInputOutlined, CustomActivatorSelect, DeletionActivationsModal},

    data: () => ({
        loading: false,
        activations: [] as ActivationToEdit[],
        initialActivationScriptsById: {} as Record<number, { messageParts: any[]; isMailOn: boolean }>,
        automailValidate: {} as any,
        selectedTag: null,
        tagList: [] as string[],
        isDeleteModalOpen: false,
        saving: false,
        activationAvailable: 0,
        copiedMenu: false,
        // объект формата { deleted, updated } для массового редактирования переменных сообщений
        messageVars: {} as any,
        copyList: {
            'staff_list': {
                name: 'Мастера',
                value: null
            },
            'activation_list': {
                name: 'Активации',
                value: null
            },
            'category_list': {
                name: 'Категории услуг',
                value: null
            },
            'service_list': {
                name: 'Услуги',
                value: null
            },
            'tag_list': {
                name: 'Тэги услуг',
                value: null
            },
            'product_list': {
                name: 'Товары',
                value: null
            }
         } as any,
    }),
    setup: () => {
        const modalStore = useModalStore()
        return {
            modalStore,
        }
    },
    watch: {
        isSuperUser: {
            immediate: true,
            handler(val) {
                if (!val && !this.isTeymurov) {
                    this.$router.push(`/project/${this.project.id}/GCB2?section=overview`)
                }
            }
        },
        copiedMenu(val) {
            if (val) {
                this.onCopiedMenuOpen()
            }
        },
        'activations': {
            handler() {
                // Форсируем обновление computed свойства
                this.$forceUpdate()
            },
            deep: true
        }
    },
    computed: {
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        isHrProject(): boolean {
            return this.project.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false
        },
        breadcrumbs(): Array<any> {
            const activationTags = this.$router.currentRoute.query.tags
            return [
                {
                    text: this.project.name,
                    to: `/project/${this.project.id}`,
                },
                {
                    text: this.isHrProject 
                        ? 'Рассылка сотрудникам' 
                        : (this.project.isChannelsRework ? 'Рассылки по базе клиентов' : 'Кого из клиентов стоит позвать'),
                    to: `/project/${this.project.id}/GCB2${activationTags ? `?tags=${activationTags}` : ''}`,
                },
            ]
        },
        tags(): Tag[] {
            return [
                { id: null, name: 'Не выбрано'},
                { id: '_manual', name: 'Ручная коммуникация'},
                { id: '_automatic', name: 'Авторассылка'},
            ].concat(
                uniq(this.activations.flatMap(el => el.tags)).map((tagName, i) => ({
                    id: tagName,
                    name: tagName,
                }))
            )
        },
        filteredActivationsIds(): number[] {
            if(this.selectedTag === '_automatic') {
                const filteredArr = this.activations.filter(el => !el.automailSettings?.extra?.automail?.isDeleted)
                return filteredArr.map(el => el.id)
            } else if(this.selectedTag === '_manual') {
                const filteredArr = this.activations.filter(el => el.automailSettings?.extra?.automail?.isDeleted)
                return filteredArr.map(el => el.id)
            }else if(this.selectedTag) {
                const filteredArr = this.activations.filter(el => el.tags.includes(this.selectedTag))
                return filteredArr.map(el => el.id)
            } else {
                return this.activations.map(el => el.id)
            }
        },
        saveButtonDisabled(): boolean {
            const emptyActivations = this.activations.length === 0
            const nameMissing = this.activations.some(el => !el.name)
            const nullData = this.activations.length === 0
            const invalidSendTime = this.hasInvalidSendTime()
            const lowTimeLimits = this.activations.some(el => toNumber(el.automailSettings?.extra?.automail?.intervalInMinutes) < 4
                && this.automailValidate[el.id]?.isAutomail)
            const nullLimitOneMailing = this.activations.some(el => !el.automailSettings?.extra?.automail?.limitOneMailing
            && this.automailValidate[el.id]?.isAutomail)
            const nullCommonLimit = this.activations.some(el => !el.automailSettings?.extra?.automail?.commonLimit
            && this.automailValidate[el.id]?.isAutomail)
            const invalidScript = this.activations.some(el => {
                return this.automailValidate[el.id]?.isAutomail && !this.automailValidate[el.id]?.isValidScripts
            })
            return emptyActivations || nameMissing || nullData || invalidScript || invalidSendTime || lowTimeLimits || nullLimitOneMailing || nullCommonLimit
        },
        saveButtonDisabledText(): string {
            const emptyActivations = this.activations.length === 0
            const nameMissing = this.activations.some(el => !el.name)
            const nullData = this.activations.length === 0
            const invalidScript = this.activations.some(el => {
                return this.automailValidate[el.id]?.isAutomail && !this.automailValidate[el.id]?.isValidScripts
            })
            const invalidSendTime = this.hasInvalidSendTime()
            const lowTimeLimits = this.activations.some(el => toNumber(el.automailSettings?.extra?.automail?.intervalInMinutes) < 4
                && this.automailValidate[el.id]?.isAutomail)
            const nullLimitOneMailing = this.activations.some(el => !el.automailSettings?.extra?.automail?.limitOneMailing
            && this.automailValidate[el.id]?.isAutomail)
            const nullCommonLimit = this.activations.some(el => !el.automailSettings?.extra?.automail?.commonLimit
            && this.automailValidate[el.id]?.isAutomail)
            if (emptyActivations) return 'Нет активаций'
            if (nullData) return 'Нет включенных активаций'
            if (nameMissing) return 'У одной из активаций некорректное название'
            if (invalidSendTime) return 'У одной из активаций некорректно настроен интервал отправки сообщений'
            if (lowTimeLimits) return 'У одной из активаций, со включенной авто отправкой, некорректный интервал между отправками (должно быть не меньше 4)'
            if (nullLimitOneMailing) return 'У одной из активаций, со включенной авто отправкой, некорректное значение сообщений за одну отправку'
            if (nullCommonLimit) return 'У одной из активаций, со включенной авто отправкой, некорректное значение сообщений за один день'
            if (invalidScript) return 'У одной из активаций, со включенной авто отправкой, введен некорректный скрипт'
        },
        isValidScripts() {
            const obj = this.activations.reduce((acc, el) => {
                const validScript = this.automailValidate[el.id]?.isValidScripts
                const isMailOn = this.automailValidate[el.id]?.isAutomail
                if (!isMailOn) {
                    acc[el.id] = true
                } else {
                    acc[el.id] = validScript
                }
                return acc
            }, {})
            return obj
        },
        isSuperUser(): boolean {
            return cfg.adminsWhitelist.includes(this.$auth.user.sub)
        },
        status(): 'active' | 'inactive' {
            return this.$router.currentRoute.query.status as 'active' | 'inactive' || 'active'
        },
        showDeleteButton(): boolean {
            return this.activations.some(el => el.needDelete)
        },
        activationsToDelete(): ActivationToEdit[] {
            const filteredActivations = this.activations.filter(el => this.filteredActivationsIds.includes(el.id))
            return filteredActivations.filter(el => el.needDelete)
        },
        automailAll: {
            get() {
                const selectedActivations = this.activations.filter(el => el.needDelete)
                if (selectedActivations.length === 0) return false
                
                // Если все выбранные активации имеют авторассылку включенную (!isDeleted)
                return selectedActivations.every(el => !el.automailSettings?.extra?.automail?.isDeleted)
            },
            set(value) {
                // Этот метод будет вызываться при ручном переключении switch
                this.toggleAutomailSelected(value)
            }
        },
        isTeymurov(): boolean {
            return cfg.teymurovId === this.$auth.user.sub
        }
    },
    methods: {
        selectAllActivations() {
            const idsToSelect = this.filteredActivationsIds
            this.activations.forEach((el, index) => {
                const shouldSelect = idsToSelect.includes(el.id)
                this.$set(this.activations[index], 'needDelete', shouldSelect)
            })
        },
        canselSelection() {
            this.activations = this.activations.map(el => ({
                ...el,
                needDelete: false
            }))
        },
        toggleAutomailSelected(e) {
            this.activations.forEach((el, index) => {
                if (el.needDelete) {
                    this.$set(this.activations[index]?.automailSettings?.extra?.automail, 'isDeleted', !e)
                    this.$set(this.automailValidate[el.id], 'isAutomail', e)
                }
            })
        },
        async handleSave(needInit: boolean) {
            this.loading = true
            this.saving = true
            const activationsTable = this.$refs.ActivationsEditTable as InstanceType<typeof ActivationsEditTable>
            
            if (activationsTable && activationsTable.saveInParent) {
                activationsTable.saveInParent();
            }
            const status = this.status
            const projectId = this.project.id
            const edits = this.activations.map(el => {
                // Используем либо preferedBot (строка), либо sendingSettings (объект) в зависимости от isChannelsRework
                let botField = {}
                if (this.project.isChannelsRework) {
                    // Для sendingSettings используем cloneDeep, так как это объект
                    if (el.extra.sendingSettings && el.extra.sendingSettings.id) {
                        botField = { sendingSettings: cloneDeep(el.extra.sendingSettings) }
                    }
                } else {
                    // Для preferedBot используем просто значение, так как это строка
                    if (el.extra.preferedBot) {
                        botField = { preferedBot: el.extra.preferedBot }
                    }
                }
                
                return omitBy(
                    {
                        activationId: el.id,
                        name: el.name,
                        desc: el.description,
                        audienceId: el.audienceSettings?.id,
                        priority: status === 'active' ? el.priority : null,
                        audienceSettings: el?.extra?.filter,
                        mailSettings: el.automailSettings?.extra?.automail,
                        messageParts: el.message?.messageParts,
                        isRunning: el.isRunning,
                        tags: el.tags,
                        ...botField
                    }, isUndefined)
            })
            let res
            try {
                const varsRes = await editActivationService.saveMessageVarsMassEdit({projectId: this.project.id, messageVars: this.messageVars})
                this.messageVars = {}
                console.log('varsRes', varsRes)
                if (Array.isArray(varsRes) && varsRes.some(
                    (el: any) => isObject(el) && (el as any).error
                )) {
                    this.$store.dispatch('callNotify', `Ошибка сохранения переменных`)
                }
                res = await bulkEditActivations({projectId, edits, status})
                if (!res.error) {
                    this.activations = res.data
                    this.$store.dispatch('callNotify', `Активации сохранены`)
                    if(needInit) {
                        await this.initData()
                        if (activationsTable && activationsTable.initData) {
                            await activationsTable.initData();
                        }
                    }
                    await setChangedActivationNotification({
                        projectId: this.project.id,
                        active: true,
                    })
                }
            } catch (err) {
            } finally {
                if(needInit) {
                    this.loading = false
                    this.saving = false
                }
            }
        },
        async saveAndExit() {
            try {
                await this.handleSave(false)
            } catch {

            } finally {
                this.goBack()
            }
            
        },
        editAudienceFilter(acivationId, value) {
            const activation = this.activations.find(el => el.id === acivationId)
            if (activation) {
                activation.extra.filter = value
            }
        },
        sortByPriority() {
            this.activations = this.activations.sort((a, b) => a.priority - b.priority)
        },
        async initData() {
            this.loading = true
            let res
            try {
                const status = this.$router.currentRoute.query.status === 'inactive' ? false : true
                const [tagsRes, activationsRes] = await Promise.all([
                    getActivationsTags(this.project.id),
                    getActivations(this.project.id, this.isHrProject, status),
                ])
                this.tagList = tagsRes.data!
                res = activationsRes
                if(!res.error) {
                    this.activations = res.data
                    if(status) {
                        this.activations = this.activations.sort((a, b) => a?.priority - b?.priority)
                    } else {
                        this.activations = this.activations.sort((a, b) => b?.order - a?.order)
                    }
                }
                for (let i in this.activations) {
                    this.$set(this.activations[i], 'needDelete', false)
                    this.activations[i].automailSettings = {}
                    if (!this.activations[i]?.extra) {
                        this.activations[i].extra = {}
                    }
                    if (this.activations[i]?.extra?.automail || !isEmpty(this.activations[i]?.extra?.automail)) {
                        this.activations[i].automailSettings.extra = {automail: this.activations[i].extra.automail}
                    } else {
                        this.$set(this.activations[i].extra, 'automail', {
                            limitOneMailing: 10,
                            commonLimit: 50,
                            intervalInMinutes: 4,
                            sendAfter: "08:00",
                            sendBefore: "21:00",
                            timezoneOffset: null,
                            isDeleted: true,
                            sendAfterDate: null,
                            sendBeforeDate: null,
                            stopOnBan: false
                        })
                        this.activations[i].automailSettings.extra = {automail: this.activations[i].extra.automail}
                    }
                    this.activations[i].message = {messageParts: this.activations[i].messageParts}
                }
                this.initialActivationScriptsById = this.activations.reduce((acc, activation) => {
                    acc[activation.id] = {
                        messageParts: cloneDeep(activation?.message?.messageParts || []),
                        isMailOn: !activation?.automailSettings?.extra?.automail?.isDeleted,
                    }
                    return acc
                }, {} as Record<number, { messageParts: any[]; isMailOn: boolean }>)
                this.automailValidate = this.activations.reduce((acc, el) => {
                    const messageParts = el?.message?.messageParts?.filter(i => !i._deleted)
                    const isValidScript = messageParts.every(i => i?.textVariant?.trim()?.length > 2)
                    const isEmpty = el?.message?.messageParts?.filter(i => !i._deleted).length < 1
                    acc[el.id] = {
                        isAutomail: false,
                        isValidScripts: isValidScript && !isEmpty,
                        isSaveButtonClicked: false,
                    }
                    return acc
                }, {})
                this.activationAvailable = 0
                getFreeSlots(this.project.id)
                    .then((slotsResp) => {
                        this.activationAvailable = slotsResp?.freeSlots || 0
                    })
                    .catch(() => {})
            } catch(err) {
            } finally {
                this.loading = false
            }
        },
        goBack () {
            this.$router.push(`/project/${this.project.id}/GCB2?section=overview`)
        },
        checkMailTextWasChangedAndSave(needExit: boolean) {
                const changeInScript = this.activations.some(el => {
                    const oldActivation = this.initialActivationScriptsById[el.id]
                    if (!oldActivation) return false
                    const isMailOnNow = !el.automailSettings?.extra?.automail?.isDeleted
                    const isMailOnBefore = oldActivation.isMailOn
                    return !isEqual(el.message.messageParts, oldActivation.messageParts) && isMailOnNow && isMailOnBefore
                })
            if ( changeInScript ) {
                    const text = 'Вы изменили текст во время работающей рассылки.\n\nНекоторые клиенты, которые уже находятся в очереди, могут получить старый текст \n\nИзменения вступят в силу в течение 15 минут'
                    const buttons: ModalButtonsParams = {
                        okName: 'СОХРАНИТЬ',
                        cancelName: 'ОТМЕНА',
                        okCallback: needExit ? () => this.saveAndExit() : () => this.handleSave(true)
                }
                this.modalStore.open({title: 'ПРЕДУПРЕЖДЕНИЕ', text, color: 'error', styleClass: 'activation-save-warning', buttons })
            } else {
                needExit ? this.saveAndExit() : this.handleSave(true)
            }
        },
        setAutomailValidate(value) {
            this.automailValidate = value
        },
        setTagInCahce() {
            localStorageCache.setProjectCache(this.project.id, 'taskListCache', 'massEditTag', this.selectedTag)
            for (let i in this.activations) {
                this.$set(this.activations[i], 'needDelete', false)
            }
        },
        openDeleteModal() {
            this.isDeleteModalOpen = true
        },
        filterAfterDelete(ids) {
            this.activations = this.activations.filter(el => !ids.includes(el.id))
            for(let i in this.activations) {
                this.$set(this.activations[i], 'needDelete', false)
            }
            const activationsTable = this.$refs.ActivationsEditTable as InstanceType<typeof ActivationsEditTable>
            if (activationsTable && activationsTable.sortByPriority) {
                activationsTable.sortByPriority();
            }
            if (activationsTable && activationsTable.normalizeEnabledPriorities) {
                activationsTable.normalizeEnabledPriorities();
            }
            const enabled = this.activations.filter(a => a.isRunning)
            const sorted = enabled.sort((a, b) => a.priority - b.priority);
            sorted.forEach((a, idx) => {
                this.$set(a, 'priority', idx + 1);
            });
            if (activationsTable && activationsTable.initData) {
                activationsTable.initData();
            }
        },
        setActivationAvailable(type: 'plus' | 'minus') {
            if(type === 'plus') {
                this.activationAvailable ++
            } else {
                this.activationAvailable --
            }
        },
        onCopiedMenuOpen() {
            Object.keys(this.copyList).forEach(item => {
                const storedData = localStorage.getItem(`massEdit-${item}`)
                if (storedData) {
                    this.copyList[item].value = JSON.parse(storedData)
                } else {
                    this.copyList[item].value = null
                }
            })
        },
        isInvalidTimeWindow(activation): boolean {
            const after = this.toMinutes(activation?.extra?.automail?.sendAfter)
            const before = this.toMinutes(activation?.extra?.automail?.sendBefore)
            if (after === null || before === null) return false
            return before <= after
        },
        hasInvalidSendTime(): boolean {
            return this.activations.some(el => this.isInvalidTimeWindow(el))
        },
        toMinutes(timeStr: string): number | null {
            if (!timeStr || typeof timeStr !== 'string') return null
            const parts = timeStr.split(':')
            if (parts.length < 2) return null
            const hours = Number(parts[0])
            const minutes = Number(parts[1])
            if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
            return hours * 60 + minutes
        },
        delCopied(item) {
            localStorage.removeItem(`massEdit-${item}`)
            this.copyList[item].value = null
        }
    },

    async created() {},
    async mounted() {
        await this.initData()
        if(this.isHrProject) {
            this.copyList['specialization_list'] = {
                name: 'Специализации', value: null
            }
            this.copyList['filial_list'] = {
                name: 'Проекты', value: null
            }
        }
        const projectCache = localStorageCache.getProjectCache(this.project.id, 'taskListCache')
        if(projectCache?.massEditTag === '_automatic' || projectCache?.massEditTag === '_manual') {
            this.selectedTag = projectCache.massEditTag
        }else if(projectCache.massEditTag && this.tags.some(el => el.id === projectCache.massEditTag)) {
            this.selectedTag = projectCache.massEditTag
        } else {
            localStorageCache.setProjectCache(this.project.id, 'taskListCache', 'massEditTag', null)
        }
    },
})
